import { useEffect, useState } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Stack,
  Alert,
} from "@mui/material"
import { AuditLogItem } from "../types"

interface RecoveryPreview {
  canApply: boolean
  snapshot: Record<string, any>
  permissions: Record<string, any>
  conflicts: string[]
  restoredId?: number
}

interface RecoveryDialogProps {
  open: boolean
  action: AuditLogItem | null
  onClose: () => void
  onRestored: (message?: string) => void
  onError: (message: string) => void
}

const RecoveryDialog = ({ open, action, onClose, onRestored, onError }: RecoveryDialogProps) => {
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<RecoveryPreview | null>(null)
  const [error, setError] = useState<string | null>(null)

  const resetState = () => {
    setPreview(null)
    setError(null)
  }

  useEffect(() => {
    if (open && action) {
      fetchPreview(false)
    } else if (!open) {
      resetState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action?.id])

  const renderPre = (payload: Record<string, any>) => (
    <pre
      style={{
        margin: 0,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontSize: "0.75rem",
        fontFamily: "monospace",
        backgroundColor: "rgba(0, 0, 0, 0.04)",
        padding: "8px",
        borderRadius: "4px",
        maxHeight: 220,
        overflow: "auto",
      }}
    >
      {JSON.stringify(payload ?? {}, null, 2)}
    </pre>
  )

  const fetchPreview = async (apply: boolean) => {
    if (!action) {
      return
    }
    try {
      setLoading(true)
      setError(null)
      const response = await fetch("/api/reports/actions/recover", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ actionId: action.id, apply }),
      })
      if (!response.ok) {
        const message = await response.text()
        throw new Error(message || "Falha ao recuperar informações do log")
      }
      const data = (await response.json()) as RecoveryPreview
      setPreview(data)
      if (apply) {
        onRestored("Registro restaurado com sucesso.")
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro inesperado ao consultar o log"
      setError(message)
      if (apply) {
        onError(message)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleRestore = async () => {
    await fetchPreview(true)
  }

  const conflicts = preview?.conflicts ?? []
  const readyToRestore = preview && conflicts.length === 0 && preview.canApply

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Restaurar exclusão</DialogTitle>
      <DialogContent dividers>
        {!preview && loading ? (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {preview && (
              <>
                <Alert severity={readyToRestore ? "success" : "warning"}>
                  {readyToRestore
                    ? "Nenhum conflito identificado. O registro pode ser restaurado."
                    : conflicts.length
                      ? "Existem conflitos que impedem a restauração automática."
                      : "Confirmação pendente para restauração."}
                </Alert>
                {conflicts.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Conflitos detectados
                    </Typography>
                    <Stack spacing={1}>
                      {conflicts.map((conflict) => (
                        <Alert key={conflict} severity="warning">
                          {conflict}
                        </Alert>
                      ))}
                    </Stack>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Dados originais
                  </Typography>
                  {renderPre(preview.snapshot ?? {})}
                </Box>
                {preview.permissions && Object.keys(preview.permissions).length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Permissões associadas
                    </Typography>
                    {renderPre(preview.permissions)}
                  </Box>
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={() => fetchPreview(false)} disabled={loading || !action}>
          Atualizar prévia
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRestore}
          disabled={loading || !action || !readyToRestore}
        >
          Restaurar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RecoveryDialog
